import {
  ShoppingCartItem,
  ShoppingCartItemCreate,
  ShoppingCartItemUpdate,
} from "@mjolnerbacken/shared/types/shopping-cart-item";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { FETCH_ERROR } from "../consts/fetch-error";
import {
  createShoppingCartItem,
  deleteShoppingCartItem,
  getShoppingCartItems,
  updateShoppingCartItem,
} from "../services/shopping-cart";

export function useShoppingCart() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["get-shopping-cart-items"],
    queryFn: () => getShoppingCartItems(),
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });

  const createMutation = useMutation({
    mutationFn: (item: ShoppingCartItemCreate) => createShoppingCartItem(item),
    onMutate: (newItem) => {
      // Get old list
      const oldList = queryClient.getQueryData<ShoppingCartItem[]>([
        "get-shopping-cart-items",
      ]);

      // Create optimistic item (with temporary ID)
      const optimisticItem: ShoppingCartItem = {
        id: `temp-${Date.now()}`,
        ...newItem,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Update list with new item
      const newList = oldList ? [...oldList, optimisticItem] : [optimisticItem];

      // Set new list
      queryClient.setQueryData(["get-shopping-cart-items"], newList);

      // Return context
      return { oldList, newList, optimisticItem };
    },
    onError: (error, variables, context) => {
      // Rollback to old list
      queryClient.setQueryData(["get-shopping-cart-items"], context?.oldList);
    },
    onSuccess: () => {
      // Invalidate query to get fresh data with real ID
      queryClient.invalidateQueries({
        queryKey: ["get-shopping-cart-items"],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, item }: { id: string; item: ShoppingCartItemUpdate }) =>
      updateShoppingCartItem(id, item),
    onMutate: ({ id, item: updateData }) => {
      // Get old list
      const oldList = queryClient.getQueryData<ShoppingCartItem[]>([
        "get-shopping-cart-items",
      ]);

      if (!oldList) return { oldList };

      // Update item in list
      const newList = oldList.map((item) =>
        item.id === id
          ? {
              ...item,
              ...updateData,
              updatedAt: new Date(),
            }
          : item,
      );

      // Set new list
      queryClient.setQueryData(["get-shopping-cart-items"], newList);

      // Return context
      return { oldList, newList };
    },
    onError: (error, variables, context) => {
      // Rollback to old list
      queryClient.setQueryData(["get-shopping-cart-items"], context?.oldList);
    },
    onSuccess: () => {
      // Invalidate query to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["get-shopping-cart-items"],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteShoppingCartItem(id),
    onMutate: (id) => {
      // Get old list
      const oldList = queryClient.getQueryData<ShoppingCartItem[]>([
        "get-shopping-cart-items",
      ]);

      if (!oldList) return { oldList };

      // Remove item from list
      const newList = oldList.filter((item) => item.id !== id);

      // Set new list
      queryClient.setQueryData(["get-shopping-cart-items"], newList);

      // Return context
      return { oldList, newList };
    },
    onError: (error, variables, context) => {
      // Rollback to old list
      queryClient.setQueryData(["get-shopping-cart-items"], context?.oldList);
    },
    onSuccess: () => {
      // Invalidate query to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["get-shopping-cart-items"],
      });
    },
  });

  const isInvalidating = useRef(false);

  return {
    // Query data and states
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isInvalidating: isInvalidating.current,
    error: query.error,
    refetch: query.refetch,

    // Create mutation
    createItem: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,

    // Update mutation
    updateItem: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    // Delete mutation
    deleteItem: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,

    invalidateQuery: async () => {
      isInvalidating.current = true;
      await queryClient.invalidateQueries({
        queryKey: ["get-shopping-cart-items"],
      });
      isInvalidating.current = false;
    },
    // Combined states
    isMutating:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
  };
}
